import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, API_BASE_URL } from '../lib/api';
import { PageHeader, Loading, ErrorBox, Badge, Modal } from '../components/ui';
import { PlatformLogo } from '../components/PlatformLogo';

// statusLabel() in apps/api/src/services/views.ts only ever returns one of these six strings —
// map each to the right tone instead of hardcoding tone="neutral" for every status (which made
// every row look identical regardless of whether an order was delivered, cancelled or refunded).
const STATUS_TONE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  'Delivered': 'success',
  'In progress': 'neutral',
  'Awaiting payment': 'warn',
  'Refunded': 'warn',
  'Declined': 'danger',
  'Cancelled': 'danger',
};
const toneFor = (label?: string) => STATUS_TONE[label ?? ''] ?? 'neutral';

export function OrdersPage() {
  const qc = useQueryClient();
  const orders = useQuery({ queryKey: ['admin-orders'], queryFn: () => adminApi.orders() });
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ['admin-order', openId], queryFn: () => adminApi.order(openId!), enabled: !!openId });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['admin-orders'] }); qc.invalidateQueries({ queryKey: ['admin-order', openId] }); };
  const cancel = useMutation({ mutationFn: () => adminApi.orderCancel(openId!), onSuccess: invalidate });
  const renotify = useMutation({ mutationFn: () => adminApi.orderRenotify(openId!), onSuccess: invalidate });

  if (orders.isLoading) return <Loading />;
  if (orders.error) return <ErrorBox error={orders.error} />;

  const rows = (orders.data as any)?.rows ?? (orders.data as any)?.items ?? [];
  const d = detail.data as any;

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="Every order, filterable and exportable"
        actions={<a className="btn-outline" href={`${API_BASE_URL}/v1/admin/orders.csv`}>Export CSV</a>}
      />
      <div className="card">
        <table>
          <thead>
            <tr><th>ID</th><th>Date</th><th>Customer</th><th>Category</th><th>Source</th><th>Store</th><th>Platform</th><th>Ours</th><th>Saved</th><th>Status</th></tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer">
                <td className="font-semibold">{r.id}</td>
                <td>{r.dateLabel}</td>
                <td>{r.customer}</td>
                <td>{r.category}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <PlatformLogo platform={r.source} size={16} />
                    <span className="text-xs capitalize">{r.source}</span>
                  </div>
                </td>
                <td>{r.store}</td>
                <td style={{ color: 'var(--text-faint)', textDecoration: 'line-through' }}>{r.platformTotal?.formatted}</td>
                <td className="font-semibold">{r.total?.formatted}</td>
                <td style={{ color: 'var(--success)' }}>{r.savings?.formatted}</td>
                <td><Badge tone={toneFor(r.statusLabel)}>{r.statusLabel}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="text-sm py-10 text-center" style={{ color: 'var(--text-faint)' }}>No orders in range</div>}
      </div>

      <Modal open={!!openId} onClose={() => setOpenId(null)} title={d?.id ?? openId ?? ''}
        footer={<>
          <button className="btn-outline" onClick={() => renotify.mutate()} disabled={renotify.isPending}>Re-notify</button>
          <button className="btn-outline" style={{ color: 'var(--danger)' }} onClick={() => cancel.mutate()} disabled={cancel.isPending}>Cancel order</button>
        </>}
      >
        {d ? (
          <div className="text-sm space-y-2">
            <div>Status: <Badge tone={toneFor(d.statusLabel)}>{d.statusLabel}</Badge></div>
            <div>Total: {d.total?.formatted}</div>
            <div className="space-y-1">
              {(d.lines ?? []).map((l: any, i: number) => <div key={i}>{l.qty}× {l.name} — {l.price?.formatted ?? l.price}</div>)}
            </div>
            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              {(d.events ?? []).map((e: any, i: number) => (
                <div key={i} className="text-xs" style={{ color: 'var(--text-faint)' }}>{e.time ?? e.at} — {e.text}</div>
              ))}
            </div>
          </div>
        ) : <div className="text-sm" style={{ color: 'var(--text-faint)' }}>Loading…</div>}
      </Modal>
    </div>
  );
}
